import { useEffect, useState } from 'react';
import { buildThemeparksEntities } from './themeparksGenericData';

const STORAGE_PREFIX = 'mpt:themeparks-content-availability:';
const UPDATE_EVENT = 'mpt:themeparks-content-availability-updated';

const getDefaultAvailability = (parkDefinition) => {
  const hasSecondaryContent = parkDefinition?.hasShowsAndRestaurants !== false;

  return {
    hasShows: hasSecondaryContent,
    hasRestaurants: hasSecondaryContent,
    isKnown: false
  };
};

const normalizeAvailability = (value, parkDefinition) => {
  const defaults = getDefaultAvailability(parkDefinition);

  if (!value || typeof value !== 'object') {
    return defaults;
  }

  return {
    hasShows: typeof value.hasShows === 'boolean' ? value.hasShows : defaults.hasShows,
    hasRestaurants: typeof value.hasRestaurants === 'boolean' ? value.hasRestaurants : defaults.hasRestaurants,
    isKnown: value.isKnown === true
  };
};

const getStorageKey = (parkSlug) => `${STORAGE_PREFIX}${parkSlug}`;

export const deriveThemeparksContentAvailability = (apiWaitTimes, parkConfig) => {
  const officialShows = Array.isArray(apiWaitTimes?.shows) ? apiWaitTimes.shows : [];
  const hasOfficialShows = officialShows.some((show) => {
    return (Array.isArray(show?.showtimes) && show.showtimes.length > 0)
      || (Array.isArray(show?.schedule) && show.schedule.length > 0);
  });

  return {
    hasShows: hasOfficialShows || buildThemeparksEntities(apiWaitTimes, parkConfig, 'SHOW').length > 0,
    hasRestaurants: buildThemeparksEntities(apiWaitTimes, parkConfig, 'RESTAURANT').length > 0,
    isKnown: true
  };
};

export const readThemeparksContentAvailability = (parkSlug, parkDefinition = null) => {
  if (!parkSlug) {
    return getDefaultAvailability(parkDefinition);
  }

  try {
    const rawValue = localStorage.getItem(getStorageKey(parkSlug));

    if (!rawValue) {
      return getDefaultAvailability(parkDefinition);
    }

    return normalizeAvailability(JSON.parse(rawValue), parkDefinition);
  } catch {
    return getDefaultAvailability(parkDefinition);
  }
};

export const writeThemeparksContentAvailability = (parkSlug, availability) => {
  if (!parkSlug) {
    return;
  }

  const normalizedAvailability = {
    hasShows: availability?.hasShows === true,
    hasRestaurants: availability?.hasRestaurants === true,
    isKnown: availability?.isKnown === true
  };

  try {
    localStorage.setItem(getStorageKey(parkSlug), JSON.stringify(normalizedAvailability));
  } catch {
    return;
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(UPDATE_EVENT));
  }
};

export const useThemeparksContentAvailability = (parkSlug, parkDefinition = null) => {
  const [availability, setAvailability] = useState(() => readThemeparksContentAvailability(parkSlug, parkDefinition));
  const hasShowsAndRestaurants = parkDefinition?.hasShowsAndRestaurants;

  useEffect(() => {
    setAvailability(readThemeparksContentAvailability(parkSlug, parkDefinition));

    if (!parkSlug || typeof window === 'undefined') {
      return undefined;
    }

    const handleAvailabilityUpdated = () => {
      setAvailability(readThemeparksContentAvailability(parkSlug, parkDefinition));
    };

    const handleStorage = (event) => {
      if (event?.key && event.key !== getStorageKey(parkSlug)) {
        return;
      }

      setAvailability(readThemeparksContentAvailability(parkSlug, parkDefinition));
    };

    window.addEventListener(UPDATE_EVENT, handleAvailabilityUpdated);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(UPDATE_EVENT, handleAvailabilityUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, [parkSlug, parkDefinition, hasShowsAndRestaurants]);

  return availability;
};
